import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import MeasuresTable from '../features/MeasuresTable';
import { routes } from '../constants';
import Accordion from '../ui/Accordion';

type Props = {
  age: number | null;
  sex: string;
  race: string | null;
  diagnosisNames: string[];
  diagnosisLabels: (string | null)[];
  measureNames: string[];
  actualScores: number[];
  actualPercentileRanks: number[];
  selectedId: string | null;
  loading: boolean;
};

/**
 * Client history page component
 * 
 * @param selectedId: string - selected client ID
 * @param loading: boolean - loading state
 * 
 * @returns Client history page element
 */
export const ClientHistoryPage = ({
  age,
  sex,
  race,
  diagnosisNames,
  diagnosisLabels,
  measureNames,
  actualScores,
  actualPercentileRanks,
  selectedId,
  loading,
}: Props): JSX.Element => {

  /* Intro text */
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const vowelNumbers = [1, 8, 11, 18, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89];
  const article = age ? (vowelNumbers.includes(age) ? 'an' : 'a') : '';
  const raceText = race ? `${race} ` : '';
  const introText = `${selectedId}, ${article} ${age}-year-old ${raceText}${sex.toLowerCase()}, was assessed by a clinician, resulting in`;

  /* Loading screen */
  if (loading) {
    return (
      <>
        <h1 className="text">Client History</h1>
        <p className="text content">Loading...</p>
      </>
    );
  }

  /* Client history page */
  return (
    <>
      <h1 className="text">Client History</h1>
      <p className="text content">{`Report Date: ${date}`}</p>
      <p className="text content">{`${introText}${diagnosisNames.length ? ":" : " no diagnosis."}`}</p>
      <ul className="text bullets content">
        {
          diagnosisNames.map((diagnosisName: string, i: number) => {
            return (
              <li key={diagnosisName}>
                {`${diagnosisName} (${diagnosisLabels[i]} by clinician)`}
                <Link
                  to={routes.DsmAssociations.path(diagnosisName)}
                  title={`View DSM criteria and mapped measures for ${diagnosisName}`}
                  aria-label={`View DSM criteria and mapped measures for ${diagnosisName}`}
                  style={{
                    display: 'inline-block',
                    marginLeft: '10px',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    background: '#eef2ff',
                    color: '#1e40af',
                    border: '1px solid #c7d2fe',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    verticalAlign: 'middle',
                  }}
                >
                  DSM-5 Criteria & Measures
                </Link>
              </li>
            )
          })
        }
      </ul>
      <Accordion
        title={<span className="emphasizedContent">Complete List of Measures Taken</span>}
        headerStyleOverrides={{ padding: "0 5%" }}
      >
        <Box sx={{
          padding: "0 0 30px 0",
          width: "100%",
        }}>
          <MeasuresTable
            measureNames={measureNames}
            actualScores={actualScores}
            percentileRanks={actualPercentileRanks.map((p) => Number((p * 100).toFixed(2)))}
            selectionEnabled={false}
            correlationVisible={false}
            correlationPercents={[]}
          />
        </Box>
      </Accordion>
      
      {/* <span className="dropdownHeader blueText sidePad">
        <span className={`dropdownArrow ${tableIsVisible && "open"}`} onClick={toggleTableSection}></span>
        <span className="emphasizedContent" onClick={toggleTableSection}>Complete List of Measures Taken</span>
      </span>
      <div className={`diagnosisCategory ${tableIsVisible ? "detailInfo" : "detailInfoInactive"}`}>
        <MeasuresTable
          measureNames={measureNames}
          actualScores={actualScores}
          percentileRanks={percentileRanks}
          selectionEnabled={false}
          correlationVisible={false}
          correlationPercents={[]}
        />
      </div> */}
    </>
  );
};

export default ClientHistoryPage;
